import React from 'react'
import ServiceLayout from '../components/ServiceLayout'
import { ArrowRight } from 'lucide-react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import ServiceCTA from '../components/ServiceCTA'

const AIMlOverviewPage = () => {
    const { t } = useTranslation()

    const features = t('aiMlOverview.features', { returnObjects: true })

    const categories = [
        {
            title: t('aiMlOverview.categories.drugDiscovery.title'),
            desc: t('aiMlOverview.categories.drugDiscovery.desc'),
            path: '/service/ai-ml/drug-discovery',
            icon: 'medication'
        },
        {
            title: t('aiMlOverview.categories.clinicalResearch.title'),
            desc: t('aiMlOverview.categories.clinicalResearch.desc'),
            path: '/service/ai-ml/clinical-research',
            icon: 'clinical_notes'
        },
        {
            title: t('aiMlOverview.categories.applied.title'),
            desc: t('aiMlOverview.categories.applied.desc'),
            path: '/service/ai-ml/applied',
            icon: 'auto_awesome'
        }
    ]

    const container = {
        hidden: { opacity: 0 },
        show: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1
            }
        }
    }

    const item = {
        hidden: { opacity: 0, y: 20 },
        show: { opacity: 1, y: 0 }
    }

    return (
        <ServiceLayout
            breadcrumbs={[
                { label: t('header.home'), path: '/' },
                { label: t('header.service'), path: '/service' },
                { label: t('header.services.aiMl.title') }
            ]}
            title={t('aiMlOverview.title')}
            subtitle={t('aiMlOverview.subtitle')}
        >
            <div className="space-y-12">
                {/* Introduction */}
                <motion.section
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                >
                    <p className="text-text-muted leading-relaxed text-lg">
                        {t('aiMlOverview.intro')}
                    </p>
                </motion.section>

                <ServiceCTA />

                {/* Value we deliver */}
                <motion.section
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15 }}
                >
                    <h2 className="text-2xl font-display font-bold text-text-main mb-6">{t('aiMlOverview.valueSectionTitle')}</h2>
                    <div className="space-y-4">
                        {Array.isArray(features) && features.map((feature, index) => (
                            <React.Fragment key={index}>
                                {feature.intro && (
                                    <div className="bio-card-tinted rounded-xl p-5 border-l-4 border-l-primary">
                                        <p className="text-text-main leading-relaxed">{feature.intro}</p>
                                    </div>
                                )}
                                <div className="bio-card-static rounded-xl p-6 border border-border-light">
                                    <h3 className="font-bold text-sm mb-2">{feature.title}</h3>
                                    <p className="text-text-muted text-sm leading-relaxed whitespace-pre-line">{feature.desc}</p>
                                </div>
                            </React.Fragment>
                        ))}
                    </div>
                </motion.section>

                {/* Service Categories */}
                <motion.section
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                >
                    <h2 className="text-2xl font-display font-bold text-text-main mb-6"></h2>
                    <motion.div
                        variants={container}
                        initial="hidden"
                        animate="show"
                        className="grid grid-cols-1 md:grid-cols-3 gap-6"
                    >
                        {categories.map((cat, i) => (
                            <motion.div key={i} variants={item} className="h-full">
                                <a
                                    href={cat.path}
                                    className="group flex flex-col h-full min-h-fit p-6 bio-card-static rounded-2xl border border-border-light hover:border-primary/30 hover:bg-primary-50 hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
                                >
                                    <div className="flex items-start justify-between mb-4">
                                        <div className="w-12 h-12 rounded-xl bg-primary-100/50 text-primary flex items-center justify-center group-hover:bg-primary group-hover:text-white transition-colors duration-300">
                                            <span className="material-symbols-outlined text-2xl">{cat.icon}</span>
                                        </div>
                                        <ArrowRight className="w-5 h-5 text-text-dim group-hover:text-primary transition-colors" />
                                    </div>
                                    
                                    <div>
                                        <h3 className="font-display font-bold text-lg text-text-main mb-2 group-hover:text-primary transition-colors">{cat.title}</h3>
                                        {cat.desc.includes('　') ? (
                                            <div className="flex flex-wrap gap-2">
                                                {cat.desc.split('　').filter(term => term.trim()).map((term, idx) => (
                                                    <span key={idx} className="px-2.5 py-1 rounded-md bg-surface-100 text-text-muted text-xs font-medium border border-border-light">
                                                        {term.trim()}
                                                    </span>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="text-sm text-text-muted leading-relaxed">{cat.desc}</p>
                                        )}
                                    </div>
                                </a>
                            </motion.div>
                        ))}
                    </motion.div>
                </motion.section>

            </div>
        </ServiceLayout>
    )
}

export default AIMlOverviewPage
